import { useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { ProjectElementMetadata } from '@deltek/specification-client/core/Models/projectElementMetadata'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setWorkspaceTemplateData } from 'api/graphQL/reactiveVars/workspaceActions'
import { isOmWorkspaceUser } from 'api/graphQL/reactiveVars/workspaceContent'
import {
    setAreSharedPropertiesVisible,
    workspaceToggles,
} from 'api/graphQL/reactiveVars/workspaceToggles'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import React from 'react'
import { useParams } from 'react-router-dom'
import { useAELocalStorage } from 'shared/utils/localStrorageUtils'
import { getFromLocalStorage } from 'shared/utils/workspaceUtils'
import CommonElementProperties from './CommonElementProperties'
import SidebarToggles, { ToggleHandlerType } from './SidebarToggles'

const useStyles = makeStyles((theme: Theme) => ({
    tabLabel: {
        fontSize: pxToRem(13),
        textTransform: 'uppercase',
        width: pxToRem(75),
        textAlign: 'justify',
        lineHeight: pxToRem(15),
    },
    defaultTextColor: {
        color: '#A78966',
    },
    selectedTabText: {
        color: '#04325B',
    },
    tabBorder: {
        borderBottom: '1px solid #DADADA',
    },
    selectedTab: {
        borderBottom: '3px solid #304959',
    },
    formSidebar: {
        minWidth: pxToRem(268),
        width: '100%',
        //    overflowY: 'auto',
        maxHeight: pxToRem(550),
        flexDirection: 'inherit',
        position: 'relative',
        top: 8,
        left: 8,
    },
    titleGridItem: {
        position: 'absolute',
        width: '100%',
        height: '45px',
        display: 'flex',
        alignItems: 'center',
        background: '#1d49661a',
    },
    title: {
        fontWeight: 600,
        fontSize: pxToRem(16),
        color: '#314959',
        marginTop: pxToRem(4),
        paddingLeft: pxToRem(8),
    },
    toggleContainer: {
        width: '100%',
        marginTop: pxToRem(45),
    },
    propertiesContainer: {
        width: '100%',
    },
}))

export interface ProjectPropertiesProps {
    drawingDesignation: string
    elementToggleHandler: (
        toggleType: ToggleHandlerType,
        value: boolean | undefined
    ) => void
    contentTeamFromStudioPage?: boolean
    setLoadingBackdrop?: (value: boolean) => void
}

export const ProjectProperties: React.FC<ProjectPropertiesProps> = ({
    drawingDesignation,
    elementToggleHandler,
    contentTeamFromStudioPage,
    setLoadingBackdrop,
}) => {
    const classes = useStyles()
    const {
        selectedElementNode: selectedProjectElementNode,
        projectMetadata,
        familyProjectMetadata,
    } = useReactiveVar(workspaceSelectedElement)

    const {
        currentUserInfo: { kpUserId: userId },
    } = useReactiveVar(userInfo)

    const elementItemValue = useElementItemStore((s) => s.elementItemValue)
    const setElementItemValue = useElementItemStore((s) => s.setElementItemValue)

    const { projectId } = useParams<{ projectId?: string }>()
    const { areSharedPropertiesVisible } = useReactiveVar(workspaceToggles)

    const { currentUserActiveFirm } = React.useContext(AppBarContext)

    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const isOMWorkspace = React.useMemo(
        () =>
            isOMUser &&
            projectId === currentUserActiveFirm?.bestPracticeProjectId,
        [projectId, isOMUser, currentUserActiveFirm]
    )
    const [isElementNotesVisible, setIsElementNotesVisible] = useAELocalStorage(
        {
            key: `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isNotesVisible`,
            userId,
            projectId: projectId as string,
            value:
                (getFromLocalStorage(
                    userId,
                    projectId!,
                    `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isNotesVisible`
                ) as boolean) ?? undefined,
        }
    )
    const [
        isElementPropertiesVisible,
        setIsElementPropertiesVisible,
    ] = useAELocalStorage({
        key: `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isPropertyVisible`,
        userId,
        projectId: projectId as string,
        value:
            (getFromLocalStorage(
                userId,
                projectId!,
                `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isPropertyVisible`
            ) as boolean) ?? undefined,
    })
    const [, setIsElementSharedVisible] = useAELocalStorage({
        key: `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isSharedVisible`,
        userId,
        projectId: projectId as string,
        value:
            (getFromLocalStorage(
                userId,
                projectId!,
                `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isSharedVisible`
            ) as boolean) ??
            selectedProjectElementNode?.elementType === 'PRODUCTFAMILY',
    })

    // Sync persisted toggle values with parent's filtering state on mount/change
    React.useEffect(() => {
        if (isElementPropertiesVisible !== undefined) {
            elementToggleHandler('property', isElementPropertiesVisible)
        }
        if (isElementNotesVisible !== undefined) {
            elementToggleHandler('notes', isElementNotesVisible)
        }
    }, [isElementPropertiesVisible, isElementNotesVisible, elementToggleHandler])

    const toggleHandler = React.useCallback(
        (name: ToggleHandlerType) => {
            setWorkspaceTemplateData(undefined)

            const toggleActions: Partial<Record<
                ToggleHandlerType,
                () => void
            >> = {
                notes: () => {
                    const toggledValue = !isElementNotesVisible
                    setIsElementNotesVisible(toggledValue)
                    elementToggleHandler(name, toggledValue)
                },
                property: () => {
                    const toggledValue = !isElementPropertiesVisible
                    setIsElementPropertiesVisible(toggledValue)
                    elementToggleHandler(name, toggledValue)
                },
                shared: () => {
                    const toggledValue = !areSharedPropertiesVisible
                    setIsElementSharedVisible(toggledValue)
                    setAreSharedPropertiesVisible(toggledValue)
                },
            }

            toggleActions[name]?.()
        },
        [
            isElementPropertiesVisible,
            areSharedPropertiesVisible,
            isElementNotesVisible,
            elementToggleHandler,
        ]
    )

    return (
        <GridContainer
            direction="column"
            data-testid="workspaceFormSideBar"
            className={classes.formSidebar}
            spacing={2}
        >
            <GridItem className={classes.titleGridItem}>
                <Typography className={classes.title} component="div">
                    PROJECT PROPERTIES
                </Typography>
            </GridItem>
            <GridItem className={classes.toggleContainer}>
                <SidebarToggles
                    toggleHandler={toggleHandler}
                    isNotesVisible={isElementNotesVisible ?? true}
                    isPropertyVisible={isElementPropertiesVisible ?? true}
                    isSharedVisible={areSharedPropertiesVisible ?? false}
                    elementType={
                        selectedProjectElementNode?.elementType as string
                    }
                    contentTeamFromStudioPage={contentTeamFromStudioPage}
                    isOMWorkspace={isOMWorkspace}
                />
            </GridItem>
            <GridItem className={classes.propertiesContainer}>
                <CommonElementProperties
                    projectElementNode={selectedProjectElementNode!}
                    projectElementMetadata={
                        projectMetadata as Partial<ProjectElementMetadata>
                    }
                    familyProjectMetadata={
                        familyProjectMetadata as Partial<ProjectElementMetadata>
                    }
                    contentTeamFromStudioPage={contentTeamFromStudioPage}
                    isOMWorkspace={isOMWorkspace}
                    setLoadingBackdrop={setLoadingBackdrop}
                />
            </GridItem>
        </GridContainer>
    )
}
